import React, { createContext, useContext, useState, useEffect } from 'react';
import { getSupportedLanguages, getUITranslations } from '../services/api';

const DEFAULT_LANGUAGES = [
  // African Languages
  { code: 'en', name: 'English', native: 'English', region: 'Global', flag: '🌐' },
  { code: 'sn', name: 'Shona', native: 'chiShona', region: 'Zimbabwe', flag: '🇿🇼' },
  { code: 'nd', name: 'Ndebele', native: 'isiNdebele', region: 'Zimbabwe/South Africa', flag: '🇿🇼' },
  { code: 'zu', name: 'Zulu', native: 'isiZulu', region: 'South Africa', flag: '🇿🇦' },
  { code: 'sw', name: 'Swahili', native: 'Kiswahili', region: 'East Africa', flag: '🇰🇪' },
  { code: 'xh', name: 'Xhosa', native: 'isiXhosa', region: 'South Africa', flag: '🇿🇦' },
  { code: 'st', name: 'Sesotho', native: 'Sesotho', region: 'South Africa/Lesotho', flag: '🇱🇸' },
  { code: 'tn', name: 'Setswana', native: 'Setswana', region: 'Botswana/South Africa', flag: '🇧🇼' },
  { code: 'af', name: 'Afrikaans', native: 'Afrikaans', region: 'South Africa', flag: '🇿🇦' },
  { code: 'yo', name: 'Yoruba', native: 'Yorùbá', region: 'Nigeria', flag: '🇳🇬' },
  { code: 'ig', name: 'Igbo', native: 'Igbo', region: 'Nigeria', flag: '🇳🇬' },
  { code: 'ha', name: 'Hausa', native: 'Hausa', region: 'Nigeria/Niger', flag: '🇳🇬' },
  { code: 'am', name: 'Amharic', native: 'አማርኛ', region: 'Ethiopia', flag: '🇪🇹' },
  { code: 'om', name: 'Oromo', native: 'Afaan Oromoo', region: 'Ethiopia', flag: '🇪🇹' },
  { code: 'so', name: 'Somali', native: 'Soomaali', region: 'Somalia', flag: '🇸🇴' },
  { code: 'rw', name: 'Kinyarwanda', native: 'Ikinyarwanda', region: 'Rwanda', flag: '🇷🇼' },
  { code: 'lg', name: 'Luganda', native: 'Oluganda', region: 'Uganda', flag: '🇺🇬' },
  { code: 'ny', name: 'Chichewa', native: 'Chichewa', region: 'Malawi', flag: '🇲🇼' },
  // Global Languages
  { code: 'fr', name: 'French', native: 'Français', region: 'Global/Africa', flag: '🇫🇷' },
  { code: 'pt', name: 'Portuguese', native: 'Português', region: 'Global/Africa', flag: '🇵🇹' },
  { code: 'ar', name: 'Arabic', native: 'العربية', region: 'North Africa/Middle East', flag: '🇸🇦' },
  { code: 'es', name: 'Spanish', native: 'Español', region: 'Global', flag: '🇪🇸' },
  { code: 'zh', name: 'Chinese', native: '中文', region: 'Global', flag: '🇨🇳' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी', region: 'India', flag: '🇮🇳' },
];

// UI translations - Comprehensive for all pages
const translations = {
  en: {
    // Navigation
    dashboard: 'Dashboard',
    courses: 'Courses',
    myCourses: 'My Courses',
    grades: 'Grades',
    attendance: 'Attendance',
    fees: 'Fees',
    achievements: 'Achievements',
    settings: 'Settings',
    logout: 'Logout',
    myChildren: 'My Children',
    exams: 'Exams',
    assignments: 'Assignments',
    messages: 'Messages',
    notifications: 'Notifications',
    // Auth
    welcome: 'Welcome',
    welcomeBack: 'Welcome back',
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',
    login: 'Login',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    register: 'Register',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    fullName: 'Full Name',
    forgotPassword: 'Forgot Password',
    resetPassword: 'Reset Password',
    createAccount: 'Create Account',
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: "Don't have an account?",
    // Actions
    submit: 'Submit',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    loading: 'Loading...',
    search: 'Search',
    filter: 'Filter',
    create: 'Create',
    upload: 'Upload',
    download: 'Download',
    share: 'Share',
    enroll: 'Enroll',
    start: 'Start',
    continue: 'Continue',
    complete: 'Complete',
    refresh: 'Refresh',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    confirm: 'Confirm',
    close: 'Close',
    viewAll: 'View All',
    learnMore: 'Learn More',
    getStarted: 'Get Started',
    // Features
    aiMentor: 'AI Mentor',
    knowledgeGraph: 'Knowledge Graph',
    voiceAvatar: 'Voice & Avatar',
    myAvatar: 'My Avatar',
    organization: 'Organization',
    participants: 'Participants',
    virtualClassroom: 'Virtual Classroom',
    studyGroups: 'Study Groups',
    learningJourney: 'Learning Journey',
    learnByTeaching: 'Learn by Teaching',
    vrLearning: 'VR Learning',
    liveResearch: 'Live Research',
    riskPrediction: 'Risk Prediction',
    credentials: 'Credentials',
    careerGuidance: 'Career Guidance',
    reportCard: 'Report Card',
    // AI Features
    getHint: 'Get Hint',
    showAnalogy: 'Show Analogy',
    askAI: 'Ask AI',
    generate: 'Generate',
    translate: 'Translate',
    listen: 'Listen',
    teachBack: 'Teach It Back',
    createQuestion: 'Create Question',
    analyze: 'Analyze',
    aiAssistant: 'AI Assistant',
    aiGenerated: 'AI Generated',
    // Learning
    concept: 'Concept',
    lesson: 'Lesson',
    lessons: 'Lessons',
    quiz: 'Quiz',
    assignment: 'Assignment',
    progress: 'Progress',
    mastery: 'Mastery',
    review: 'Review',
    practice: 'Practice',
    course: 'Course',
    topic: 'Topic',
    subject: 'Subject',
    // Content Types
    video: 'Video',
    audio: 'Audio',
    image: 'Image',
    text: 'Text',
    slides: 'Slides',
    interactive: 'Interactive',
    avatarTutor: 'Avatar Tutor',
    // Tutor
    selectTutor: 'Select Your Tutor',
    cloneVoice: 'Clone Voice',
    changeAvatar: 'Change Avatar',
    speakToMe: 'Speak to Me',
    // Dashboard
    currentGPA: 'Current GPA',
    enrolledCourses: 'Enrolled Courses',
    attendanceRate: 'Attendance Rate',
    pendingFees: 'Pending Fees',
    quickActions: 'Quick Actions',
    recentActivity: 'Recent Activity',
    upcomingClasses: 'Upcoming Classes',
    totalStudents: 'Total Students',
    totalTeachers: 'Total Teachers',
    activeCourses: 'Active Courses',
    // Settings
    profileInformation: 'Profile Information',
    notificationPreferences: 'Notification Preferences',
    appearance: 'Appearance',
    security: 'Security',
    language: 'Language',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    emailNotifications: 'Email Notifications',
    pushNotifications: 'Push Notifications',
    // Parent Portal
    parentPortal: 'Parent Portal',
    children: 'Children',
    childProgress: 'Child Progress',
    linkChild: 'Link Child',
    courseProgress: 'Course Progress',
    // Teacher
    teacherDashboard: 'Teacher Dashboard',
    createCourse: 'Create Course',
    manageCourses: 'Manage Courses',
    gradeAssignments: 'Grade Assignments',
    studentList: 'Student List',
    // Organization
    orgDashboard: 'Organization Dashboard',
    enrollParticipant: 'Enroll Participant',
    bulkEnroll: 'Bulk Enroll',
    staffManagement: 'Staff Management',
    // Messages
    success: 'Success',
    error: 'Error',
    noData: 'No data available',
    confirmDelete: 'Are you sure you want to delete?',
    savedSuccessfully: 'Saved successfully',
    updatedSuccessfully: 'Updated successfully',
    deletedSuccessfully: 'Deleted successfully',
    // Academic Levels (Zimbabwe)
    grade: 'Grade',
    form: 'Form',
    undergraduate: 'Undergraduate',
    postgraduate: 'Postgraduate',
    // Course Types
    freeCourse: 'Free Course',
    certificate: 'Certificate',
    certification: 'Certification',
    diploma: 'Diploma',
    threeDayCourse: '3-Day Course',
    // Payment
    payNow: 'Pay Now',
    paymentMethod: 'Payment Method',
    cash: 'Cash',
    ecocash: 'Ecocash',
    oneMoney: 'OneMoney',
    innBucks: 'InnBucks',
    paynow: 'Paynow',
    mastercard: 'Mastercard',
    paymentConfirmed: 'Payment Confirmed',
    // Study Groups
    createGroup: 'Create Group',
    joinGroup: 'Join Group',
    groupChat: 'Group Chat',
    findMatches: 'Find Matches',
    // Zoom/Meetings
    joinMeeting: 'Join Meeting',
    scheduleMeeting: 'Schedule Meeting',
    startMeeting: 'Start Meeting',
    liveMeeting: 'Live',
    scheduledMeeting: 'Scheduled',
    completedMeeting: 'Completed',
  },
  sn: {
    // Navigation
    dashboard: 'Peji Rekutanga',
    courses: 'Zvidzidzo',
    myCourses: 'Zvidzidzo Zvangu',
    grades: 'Mamariki',
    attendance: 'Kupinda',
    fees: 'Mari Yechikoro',
    achievements: 'Zvakaitwa',
    settings: 'Zvigadziriso',
    logout: 'Buda',
    myChildren: 'Vana Vangu',
    exams: 'Bvunzo',
    assignments: 'Mabasa',
    messages: 'Mashoko',
    notifications: 'Zviziviso',
    // Auth
    welcome: 'Titambire',
    welcomeBack: 'Titambire zvakare',
    goodMorning: 'Mangwanani akanaka',
    goodAfternoon: 'Masikati akanaka',
    goodEvening: 'Manheru akanaka',
    login: 'Pinda',
    signIn: 'Pinda',
    signUp: 'Nyoresa',
    register: 'Nyoresa',
    email: 'Email',
    password: 'Pasiwedhi',
    confirmPassword: 'Simbisa Pasiwedhi',
    fullName: 'Zita Rizere',
    forgotPassword: 'Wakanganwa Pasiwedhi',
    resetPassword: 'Dzorera Pasiwedhi',
    createAccount: 'Gadzira Account',
    // Actions
    submit: 'Tumira',
    cancel: 'Kanzura',
    save: 'Chengetedza',
    delete: 'Dzima',
    edit: 'Gadzirisa',
    view: 'Ona',
    loading: 'Kuverenga...',
    search: 'Tsvaga',
    filter: 'Sefa',
    create: 'Gadzira',
    upload: 'Isa',
    download: 'Dhawunirodha',
    share: 'Govera',
    enroll: 'Nyoresa',
    start: 'Tanga',
    continue: 'Enderera',
    complete: 'Pedza',
    refresh: 'Refresha',
    back: 'Dzoka',
    next: 'Mberi',
    previous: 'Shure',
    confirm: 'Simbisa',
    close: 'Vhara',
    viewAll: 'Ona Zvose',
    learnMore: 'Dzidza Zvimwe',
    getStarted: 'Tanga Zvino',
    // Features
    aiMentor: 'Mudzidzisi weAI',
    knowledgeGraph: 'Mufananidzo weRuzivo',
    voiceAvatar: 'Inzwi neAvatar',
    myAvatar: 'Avatar Yangu',
    organization: 'Sangano',
    participants: 'Vatanhi',
    virtualClassroom: 'Kirasi yeInternet',
    studyGroups: 'Mapoka Ekudzidza',
    learningJourney: 'Rwendo Rwekudzidza',
    learnByTeaching: 'Dzidza Nekudzidzisa',
    vrLearning: 'Kudzidza kweVR',
    liveResearch: 'Tsvakurudzo Yepanguva',
    riskPrediction: 'Kufembera Ngozi',
    credentials: 'Zvitupa',
    careerGuidance: 'Kutungamira Basa',
    reportCard: 'Kadhi Remamariki',
    // AI Features
    getHint: 'Wana Rubatsiro',
    showAnalogy: 'Ratidza Mufananidzo',
    askAI: 'Bvunza AI',
    generate: 'Gadzira',
    translate: 'Shandura',
    listen: 'Teerera',
    aiAssistant: 'Mubatsiri weAI',
    // Learning
    concept: 'Pfungwa',
    lesson: 'Chidzidzo',
    lessons: 'Zvidzidzo',
    quiz: 'Quiz',
    assignment: 'Basa',
    progress: 'Kufamba',
    mastery: 'Kuziva Zvakanaka',
    course: 'Chidzidzo',
    topic: 'Nyaya',
    subject: 'Chidzidzo',
    // Content Types
    video: 'Vhidhiyo',
    audio: 'Inzwi',
    image: 'Mufananidzo',
    text: 'Zvakanyorwa',
    slides: 'Masirayidi',
    avatarTutor: 'Mudzidzisi Avatar',
    // Dashboard
    currentGPA: 'GPA Yepanguva Ino',
    enrolledCourses: 'Zvidzidzo Zvakanyoreswa',
    attendanceRate: 'Mwero Wekupinda',
    pendingFees: 'Mari Isati Yabhadharwa',
    quickActions: 'Zviito Zvekukurumidza',
    totalStudents: 'Vadzidzi Vose',
    totalTeachers: 'Vadzidzisi Vose',
    activeCourses: 'Zvidzidzo Zvinoshanda',
    // Settings
    profileInformation: 'Mashoko eProfile',
    notificationPreferences: 'Zviziviso Zvinodiwa',
    appearance: 'Maitiro',
    security: 'Chengeteko',
    language: 'Mutauro',
    changePassword: 'Shandura Pasiwedhi',
    // Parent Portal
    parentPortal: 'Potal yeVabereki',
    children: 'Vana',
    linkChild: 'Batanidza Mwana',
    // Messages
    success: 'Zvabudirira',
    error: 'Kukanganisa',
    noData: 'Hapana Data',
    savedSuccessfully: 'Zvachengetedzwa',
    // Payment
    payNow: 'Bhadhara Iye Zvino',
    paymentMethod: 'Nzira Yekubhadhara',
    cash: 'Mari',
    paymentConfirmed: 'Kubhadhara Kwasimbiswa',
    // Study Groups
    createGroup: 'Gadzira Boka',
    joinGroup: 'Pinda muBoka',
    findMatches: 'Tsvaka Vakafanana',
    // Meetings
    joinMeeting: 'Pinda muMusangano',
    liveMeeting: 'Yakatanga',
    scheduledMeeting: 'Yakagadzirwa',
  },
  sw: {
    dashboard: 'Dashibodi',
    courses: 'Kozi',
    myCourses: 'Kozi Zangu',
    grades: 'Daraja',
    attendance: 'Mahudhurio',
    fees: 'Ada',
    achievements: 'Mafanikio',
    settings: 'Mipangilio',
    logout: 'Toka',
    myChildren: 'Watoto Wangu',
    exams: 'Mitihani',
    assignments: 'Kazi',
    messages: 'Ujumbe',
    notifications: 'Arifa',
    welcome: 'Karibu',
    welcomeBack: 'Karibu tena',
    goodMorning: 'Habari ya asubuhi',
    goodAfternoon: 'Habari ya mchana',
    goodEvening: 'Habari ya jioni',
    login: 'Ingia',
    signIn: 'Ingia',
    signUp: 'Jiandikishe',
    register: 'Jiandikishe',
    email: 'Barua pepe',
    password: 'Nenosiri',
    fullName: 'Jina Kamili',
    submit: 'Wasilisha',
    cancel: 'Ghairi',
    save: 'Hifadhi',
    loading: 'Inapakia...',
    search: 'Tafuta',
    filter: 'Chuja',
    create: 'Unda',
    upload: 'Pakia',
    download: 'Pakua',
    share: 'Shiriki',
    enroll: 'Jiandikishe',
    start: 'Anza',
    continue: 'Endelea',
    complete: 'Maliza',
    aiMentor: 'Mshauri wa AI',
    knowledgeGraph: 'Grafu ya Maarifa',
    voiceAvatar: 'Sauti na Avatar',
    organization: 'Shirika',
    participants: 'Washiriki',
    virtualClassroom: 'Darasa Pepe',
    studyGroups: 'Vikundi vya Kusoma',
    learningJourney: 'Safari ya Kujifunza',
    learnByTeaching: 'Jifunze kwa Kufundisha',
    getHint: 'Pata Dokezo',
    askAI: 'Uliza AI',
    translate: 'Tafsiri',
    listen: 'Sikiliza',
    video: 'Video',
    audio: 'Sauti',
    image: 'Picha',
    text: 'Maandishi',
    currentGPA: 'GPA ya Sasa',
    enrolledCourses: 'Kozi Zilizosajiliwa',
    attendanceRate: 'Kiwango cha Mahudhurio',
    pendingFees: 'Ada Inayosubiri',
    totalStudents: 'Wanafunzi Wote',
    totalTeachers: 'Walimu Wote',
    success: 'Imefanikiwa',
    error: 'Hitilafu',
    payNow: 'Lipa Sasa',
    createGroup: 'Unda Kikundi',
    joinGroup: 'Jiunge na Kikundi',
    joinMeeting: 'Jiunge na Mkutano',
  },
  zu: {
    dashboard: 'Ikhasi Lokuqala',
    courses: 'Izifundo',
    myCourses: 'Izifundo Zami',
    grades: 'Amamaki',
    attendance: 'Ukuba Khona',
    fees: 'Imali Yesikole',
    achievements: 'Ukuphumelela',
    settings: 'Izilungiselelo',
    logout: 'Phuma',
    myChildren: 'Abantwana Bami',
    exams: 'Izivivinyo',
    assignments: 'Imisebenzi',
    messages: 'Imilayezo',
    notifications: 'Izaziso',
    welcome: 'Siyakwamukela',
    welcomeBack: 'Siyakwamukela futhi',
    goodMorning: 'Sawubona ekuseni',
    goodAfternoon: 'Sawubona emini',
    goodEvening: 'Sawubona kusihlwa',
    login: 'Ngena',
    signIn: 'Ngena',
    signUp: 'Bhalisa',
    register: 'Bhalisa',
    email: 'I-imeyili',
    password: 'Iphasiwedi',
    fullName: 'Igama Eligcwele',
    submit: 'Thumela',
    cancel: 'Khansela',
    save: 'Gcina',
    loading: 'Kuyalayisha...',
    search: 'Sesha',
    create: 'Dala',
    upload: 'Layisha',
    share: 'Yabelana',
    enroll: 'Bhalisa',
    start: 'Qala',
    continue: 'Qhubeka',
    aiMentor: 'Umfundisi we-AI',
    knowledgeGraph: 'Igrafu Yolwazi',
    voiceAvatar: 'Izwi ne-Avatar',
    organization: 'Inhlangano',
    participants: 'Abahlanganyeli',
    virtualClassroom: 'Ikilasi Lenethiweki',
    studyGroups: 'Amaqembu Okufunda',
    currentGPA: 'I-GPA Yamanje',
    enrolledCourses: 'Izifundo Ezibhalisiwe',
    success: 'Kuphumelele',
    error: 'Iphutha',
    payNow: 'Khokha Manje',
    joinMeeting: 'Joyina Umhlangano',
  },
  nd: {
    dashboard: 'Ikhasi Lokuqala',
    courses: 'Izifundo',
    grades: 'Amamaki',
    attendance: 'Ukuba Khona',
    fees: 'Imali Yesikolo',
    achievements: 'Impumelelo',
    settings: 'Izilungiselelo',
    logout: 'Phuma',
    welcome: 'Siyakwamukela',
    login: 'Ngena',
    register: 'Bhalisa',
    email: 'I-imeyili',
    password: 'Iphasiwedi',
    fullName: 'Ibizo Eligcweleyo',
    submit: 'Thumela',
    cancel: 'Khansela',
    save: 'Gcina',
    loading: 'Kuyalayisha...',
    search: 'Sesha',
    filter: 'Hlunga',
    aiMentor: 'Umfundisi we-AI',
    knowledgeGraph: 'Igrafu Yolwazi',
    voiceAvatar: 'Ilizwi le-Avatar',
    organization: 'Inhlangano',
    participants: 'Abahlanganyeli',
  },
  fr: {
    dashboard: 'Tableau de bord',
    courses: 'Cours',
    myCourses: 'Mes cours',
    grades: 'Notes',
    attendance: 'Présence',
    fees: 'Frais',
    achievements: 'Réussites',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    myChildren: 'Mes enfants',
    exams: 'Examens',
    assignments: 'Devoirs',
    messages: 'Messages',
    notifications: 'Notifications',
    welcome: 'Bienvenue',
    welcomeBack: 'Bon retour',
    goodMorning: 'Bonjour',
    goodAfternoon: 'Bon après-midi',
    goodEvening: 'Bonsoir',
    login: 'Connexion',
    signIn: 'Se connecter',
    signUp: "S'inscrire",
    register: "S'inscrire",
    email: 'E-mail',
    password: 'Mot de passe',
    fullName: 'Nom complet',
    submit: 'Soumettre',
    cancel: 'Annuler',
    save: 'Enregistrer',
    loading: 'Chargement...',
    search: 'Rechercher',
    create: 'Créer',
    upload: 'Télécharger',
    share: 'Partager',
    enroll: "S'inscrire",
    start: 'Commencer',
    continue: 'Continuer',
    aiMentor: 'Mentor IA',
    knowledgeGraph: 'Graphe de connaissances',
    organization: 'Organisation',
    virtualClassroom: 'Classe virtuelle',
    studyGroups: "Groupes d'étude",
    currentGPA: 'Moyenne actuelle',
    success: 'Succès',
    error: 'Erreur',
    payNow: 'Payer maintenant',
    joinMeeting: 'Rejoindre la réunion',
  },
  pt: {
    dashboard: 'Painel',
    courses: 'Cursos',
    myCourses: 'Meus Cursos',
    grades: 'Notas',
    attendance: 'Frequência',
    fees: 'Taxas',
    settings: 'Configurações',
    logout: 'Sair',
    welcome: 'Bem-vindo',
    login: 'Entrar',
    register: 'Registrar',
    email: 'E-mail',
    password: 'Senha',
    fullName: 'Nome Completo',
    submit: 'Enviar',
    cancel: 'Cancelar',
    save: 'Salvar',
    loading: 'Carregando...',
    search: 'Pesquisar',
    create: 'Criar',
    success: 'Sucesso',
    error: 'Erro',
  },
  ar: {
    dashboard: 'لوحة القيادة',
    courses: 'الدورات',
    myCourses: 'دوراتي',
    grades: 'الدرجات',
    attendance: 'الحضور',
    fees: 'الرسوم',
    settings: 'الإعدادات',
    logout: 'تسجيل الخروج',
    welcome: 'مرحبا',
    login: 'تسجيل الدخول',
    register: 'التسجيل',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    fullName: 'الاسم الكامل',
    submit: 'إرسال',
    cancel: 'إلغاء',
    save: 'حفظ',
    loading: 'جاري التحميل...',
    search: 'بحث',
    create: 'إنشاء',
    success: 'نجاح',
    error: 'خطأ',
  },
  es: {
    dashboard: 'Panel',
    courses: 'Cursos',
    myCourses: 'Mis Cursos',
    grades: 'Calificaciones',
    attendance: 'Asistencia',
    fees: 'Tarifas',
    settings: 'Configuración',
    logout: 'Cerrar sesión',
    welcome: 'Bienvenido',
    login: 'Iniciar sesión',
    register: 'Registrarse',
    email: 'Correo electrónico',
    password: 'Contraseña',
    fullName: 'Nombre completo',
    submit: 'Enviar',
    cancel: 'Cancelar',
    save: 'Guardar',
    loading: 'Cargando...',
    search: 'Buscar',
    create: 'Crear',
    success: 'Éxito',
    error: 'Error',
  },
  xh: {
    dashboard: 'Ibhodi',
    courses: 'Izifundo',
    settings: 'Iisetingi',
    logout: 'Phuma',
    welcome: 'Wamkelekile',
  },
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('eduos_language') || 'en';
  });
  const [languages, setLanguages] = useState(DEFAULT_LANGUAGES);

  useEffect(() => {
    localStorage.setItem('eduos_language', language);
    document.documentElement.lang = language;
  }, [language]);

  // Fetch languages from backend on mount
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const data = await getSupportedLanguages();
        if (data && data.length > 0) {
          const langWithFlags = data.map(lang => ({
            ...lang,
            flag: DEFAULT_LANGUAGES.find(l => l.code === lang.code)?.flag || '🌍'
          }));
          setLanguages(langWithFlags);
        }
      } catch (error) {
        console.error('Failed to fetch languages:', error);
      }
    };
    fetchLanguages();
  }, []);

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const changeLanguage = (newLang) => {
    if (languages.find(l => l.code === newLang)) {
      setLanguage(newLang);
    }
  };

  const currentLanguage = languages.find(l => l.code === language) || languages[0];

  // Group languages by region for UI
  const languagesByRegion = languages.reduce((acc, lang) => {
    const region = lang.region || 'Other';
    if (!acc[region]) acc[region] = [];
    acc[region].push(lang);
    return acc;
  }, {});

  const value = {
    language,
    currentLanguage,
    languages,
    languagesByRegion,
    t,
    changeLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
